<template>
  <div class="teaser" data-testid="teaser-deck" @click="goToPresentation">
    <div class="teaser__list">
      <DeckSiteSlide
        v-if="slide && slide.type === 'S'"
        class="teaser__item"
        auto-rotate
        hide-slide-title
        :slide="slide"
        :camera-id="slide.camera_id"
        :company-id="deck.company.id"
        :site="{ siteId: slide.site_id } as SiteState"
        :three-d-viewer="threeDViewer"
        data-testid="teaser-site-slide"
      />
      <DeckThreeDSlide
        v-if="slide && slide.type === 'M'"
        class="teaser__item"
        :uuid="uuid"
        :slide="slide"
        :deck-id="deck.id"
        :company-id="deck.company.id"
        :three-d-viewer="threeDViewer"
        data-testid="teaser-3D-slide"
        @update:load-status="isThreeDLoading = $event"
      />
    </div>
    <section v-if="!isThreeDLoading" class="teaser__container" inert>
      <header class="teaser__header">
        <h2>Interactive Content</h2>
        <p>{{ deck.title }}</p>
      </header>
      <div class="teaser__actions">
        <VButton class="teaser__btn" type="button" data-testid="teaser-presentation-btn">
          <ExternalLinkIcon class="teaser__icon" />
          <p>View Presentation</p>
        </VButton>
      </div>
      <footer class="mt-6 mb-10">
        <p class="text-xs leading-4 font-normal text-white flex gap-2 items-center">
          powered by
          <VrifyLogo width="100" />
        </p>
      </footer>
    </section>
  </div>
</template>

<script lang="ts" setup>
import type { Slide } from '@/entities/deck'
import type { Deck, IncludeCompany } from '@/entities/deck'
import type { DeckWithEmbed } from '@/entities/embed'
import VrifyLogo from '@/assets/images/vrify-logo.svg'
import ExternalLinkIcon from '@/components/icons/ExternalLinkIcon.svg'
import type { DeepReadonly } from 'vue'

const props = defineProps({
  teaserSlides: {
    type: Array as PropType<DeepReadonly<Slide[]>>,
    required: true,
  },
  deck: {
    type: Object as PropType<DeepReadonly<DeckWithEmbed & IncludeCompany> | DeepReadonly<Deck & IncludeCompany>>,
    required: true,
  },
  intervalTime: {
    type: Number,
    default: 5000,
  },
  uuid: {
    type: String,
    default: null,
  },
  stayInFrame: {
    type: Boolean,
    default: false,
  },
  analyticsNamespace: {
    type: String,
    required: true,
  },
  analyticsHaveFired: {
    type: Object as PropType<{ teaserPageLoad: boolean; teaserClickViewPresentation: boolean; deckPageLoad: boolean }>,
    default: () => ({
      teaserPageLoad: false,
      teaserClickViewPresentation: false,
      deckPageLoad: false,
    }),
  },
  threeDViewer: {
    type: Object as PropType<UseThreeDViewer>,
    required: true,
  },
})

const emit = defineEmits(['set-active-scene', 'open-full-deck', 'analytics-fired'])
const slide = computed(() => props.teaserSlides[0]) // Because this is a the teaser list the first slide should be the main slide. It'll either be an array of 1 site slide or 2 camera slides
const isThreeDLoading = ref(false)
const { $analytics } = useNuxtApp()

const { startSlideAutoPlay } = useTeaserSlides({
  teaserSlides: props.teaserSlides,
  intervalTime: props.intervalTime,
  setActiveScene: (...e) => emit('set-active-scene', ...e),
})

function isDeckWithEmbed(deck: any): deck is DeepReadonly<DeckWithEmbed & IncludeCompany> {
  return 'partner_id' in deck && 'partner_name' in deck && 'embed_name' in deck
}

onMounted(() => {
  if (!props.analyticsHaveFired?.teaserPageLoad && isDeckWithEmbed(props.deck)) {
    $analytics.track(`${props.analyticsNamespace}PageLoad`, {
      deckId: props.deck.id,
      deckName: props.deck.title,
      companyId: props.deck.company.id,
      companyName: props.deck.company.name,
      partnerId: props.deck.partner_id,
      partnerName: props.deck.partner_name,
      embedLocation: props.deck.embed_name,
    })

    emit('analytics-fired', 'teaserPageLoad')
  }
  startSlideAutoPlay()
})

function goToPresentation() {
  if (isThreeDLoading.value) return

  if (!props.analyticsHaveFired?.teaserClickViewPresentation && isDeckWithEmbed(props.deck)) {
    $analytics.track(`${props.analyticsNamespace}ClickViewPresentation`, {
      deckId: props.deck.id,
      deckName: props.deck.title,
      companyId: props.deck.company.id,
      companyName: props.deck.company.name,
      partnerId: props.deck.partner_id,
      partnerName: props.deck.partner_name,
      embedLocation: props.deck.embed_name,
    })

    emit('analytics-fired', 'teaserClickViewPresentation')
  }

  emit('open-full-deck')
}
</script>

<style lang="stylus" scoped>
.teaser
  @apply h-screen w-screen cursor-pointer

  &__list
    @apply absolute left-0 w-full h-full -z-10

  &__container
    @apply absolute flex flex-col w-full h-full items-center z-10 text-center text-white bg-opacity-30 bg-gray-900 px-9 justify-between

  &__header
    @apply w-screen mb-4

    h2
      @apply text-base leading-6 font-semibold mb-1 pt-4

    p
      @apply text-xs font-normal whitespace-nowrap overflow-ellipsis overflow-hidden px-4 pb-4 mb-0

  &__actions
    @apply gap-3 flex flex-row flex-wrap justify-center

  &__btn
    @apply flex flex-row flex-nowrap justify-center items-center rounded-md bg-white opacity-80 p-4 cursor-pointer gap-3
    width 258px

    & > p
      @apply text-sm font-semibold m-0

  &__icon
    @apply text-gray-900
    width 18px
    height 16px
</style>
